import React, { useEffect, useState } from "react";
import { Row, Col } from "reactstrap";
import { FaSave, FaTimes } from "react-icons/fa";
import { FaAngleDoubleUp, FaAngleDoubleDown } from "react-icons/fa";
import { AiFillPlusCircle, AiOutlinePlusCircle } from "react-icons/ai";
import { TiDeleteOutline } from "react-icons/ti";
import { GoDotFill } from "react-icons/go";
import { MdDeleteOutline, MdOutlineFileCopy } from "react-icons/md";
import useCustomMutation from "../../../../Hooks/useCustomMutation";
import {
  appendOptInDisclaimerFields,
  isSystemDisclaimerField,
} from "../../../../constants/optInDisclaimerFields";
import OptInDisclaimerPreview from "./OptInDisclaimerPreview";

const EditRegistrationForm = ({
  dataForm,
  token,
  eventID,
  setIsOffcanvasOpen,
  formID,
}) => {
  const [formContent, setFormContent] = useState([]);
  const [editedField, setEditedField] = useState(null);
  const [textField, setTextField] = useState("");
  const [fieldCount, setFieldCount] = useState(1);
  const [submitError, setSubmitError] = useState(null);

  const key = ["eventForm", eventID];
  const mutation = useCustomMutation(key);

  // ✅ Load form data
  useEffect(() => {
    const additionalFields = dataForm
      ?.filter((field) => !isSystemDisclaimerField(field))
      ?.map((field) => ({
        id: field._id,
        label: field.label,
        list: field.list,
        name: field.name,
        question_type: field.question_type,
      }));
    setFormContent(additionalFields || []);
  }, [dataForm]);

  // ✅ Add new dynamic question
  const addQuestion = () => {
    const newField = {
      id: `question_${Date.now()}`,
      name: `question_${formContent.length + 1}`,
      label: `Untitled question #${fieldCount}`,
      question_type: "short_answer",
      list: [],
    };
    setFormContent([...formContent, newField]);
    setFieldCount((prevCount) => prevCount + 1);
  };

  // ✅ Duplicate question
  const duplicateQuestion = (index) => {
    const fieldToDuplicate = formContent[index];
    const newField = {
      ...fieldToDuplicate,
      id: `question_${Date.now()}`,
      label: `Untitled question #${fieldCount}`,
    };
    const updatedFormContent = [...formContent];
    updatedFormContent.splice(index + 1, 0, newField);
    setFormContent(updatedFormContent);
    setFieldCount((prevCount) => prevCount + 1);
  };

  // ✅ Delete question
  const deleteQuestion = (index) => {
    if (index >= 0) {
      const updatedFormContent = formContent.filter((_, i) => i !== index);
      setFormContent(updatedFormContent);
    }
  };

  // ✅ Edit field label
  const editField = (fieldId, fieldLabel) => {
    const updatedFields = formContent.map((field) =>
      field.id === fieldId ? { ...field, label: fieldLabel } : field
    );
    setFormContent(updatedFields);
  };

  // ✅ Edit field type
  const editFieldType = (fieldId, fieldType) => {
    const updatedFields = formContent.map((field) => {
      if (field.id === fieldId) {
        return {
          ...field,
          question_type: fieldType,
          list: fieldType === "options" ? field.list : [],
        };
      }
      return field;
    });
    setFormContent(updatedFields);
  };

  // ✅ Add option to field
  const addFieldOption = (fieldId, option) => {
    if (!option?.trim()) return;
    const updatedFields = formContent.map((field) =>
      field.id === fieldId
        ? { ...field, list: [...field.list, option.trim()] }
        : field
    );
    setFormContent(updatedFields);
    setTextField("");
  };

  // ✅ Remove option
  const removeFieldOption = (fieldId, optionIndex) => {
    const updatedFields = formContent.map((field) =>
      field.id === fieldId
        ? { ...field, list: field.list.filter((_, idx) => idx !== optionIndex) }
        : field
    );
    setFormContent(updatedFields);
  };

  // ✅ Move field up
  const moveFieldUp = (index) => {
    if (index <= 0) return;
    const updatedFormContent = [...formContent];
    [updatedFormContent[index - 1], updatedFormContent[index]] = 
    [updatedFormContent[index], updatedFormContent[index - 1]];
    setFormContent(updatedFormContent);
  };

  // ✅ Move field down
  const moveFieldDown = (index) => {
    if (index >= formContent.length - 1) return;
    const updatedFormContent = [...formContent];
    [updatedFormContent[index + 1], updatedFormContent[index]] = 
    [updatedFormContent[index], updatedFormContent[index + 1]];
    setFormContent(updatedFormContent);
  };

  // ✅ ✅ WORKING UPDATE FUNCTION - Matches Version 1 pattern
  const handleUpdate = () => {
    setSubmitError(null);
    
    // Validate: at least one field must exist
    if (!formContent?.length) {
      setSubmitError("Form must have at least one field");
      return;
    }

    const savedData = appendOptInDisclaimerFields(
      formContent?.map(({ id, ...rest }) => rest)
    );
    
    const Method = "PATCH";
    const url = `event-form/${formID}`;
    const raw = JSON.stringify({ customFields: savedData });
    
    const data = {
      endPoint: url,
      bodyContent: raw,
      Method: Method,
      displayMessage: "Registration form successfully updated",
      token: token,
      isJSON: null,
    };

    mutation.mutate(data);
  };

  useEffect(() => {
    if (mutation?.isSuccess && mutation?.data?.status === "success") {
      setIsOffcanvasOpen(false);
    }
  }, [mutation?.isSuccess, mutation?.data, setIsOffcanvasOpen]);

  useEffect(() => {
    if (mutation?.isError) {
      const errorMsg =
        mutation?.error?.message ||
        "Failed to update form. Please try again.";
      setSubmitError(errorMsg);
    }
  }, [mutation?.isError, mutation?.error]);

  // ✅ Cancel handler
  const handleCancel = () => {
    setIsOffcanvasOpen(false);
  };

  const isDisabled = mutation?.isLoading || !formID || !token;

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
      padding: "24px",
      paddingBottom: "140px",
    }}>
      {/* ✅ Action Buttons - Top Right */}
      <div style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: "12px",
        marginBottom: "24px",
        padding: "16px 20px",
        background: "white",
        borderRadius: "14px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      }}>
        <button
          onClick={handleCancel}
          disabled={mutation?.isLoading}
          style={{
            padding: "10px 20px",
            borderRadius: "10px",
            border: "2px solid #e2e8f0",
            backgroundColor: "white",
            color: "#64748b",
            cursor: mutation?.isLoading ? "not-allowed" : "pointer",
            fontSize: "13px",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            transition: "all 0.3s ease",
          }}
          onMouseEnter={(e) => {
            if (!mutation?.isLoading) {
              e.currentTarget.style.backgroundColor = "#f8fafc";
              e.currentTarget.style.borderColor = "#cbd5e1";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "white";
            e.currentTarget.style.borderColor = "#e2e8f0";
          }}
        >
          <FaTimes size={12} />
          Cancel
        </button>
        
        {/* ✅ WORKING UPDATE BUTTON - Beautiful UI + Reliable Functionality */}
        <button
          onClick={handleUpdate}
          disabled={isDisabled}
          style={{
            padding: "10px 24px",
            borderRadius: "10px",
            border: "none",
            background: isDisabled 
              ? "#cbd5e1" 
              : "linear-gradient(135deg, #f68a20 0%, #fb923c 100%)",
            color: "white",
            cursor: isDisabled ? "not-allowed" : "pointer",
            fontSize: "13px",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            boxShadow: isDisabled ? "none" : "0 4px 12px rgba(246, 138, 32, 0.3)",
            transition: "all 0.3s ease",
            opacity: isDisabled ? 0.7 : 1,
          }}
          onMouseEnter={(e) => {
            if (!isDisabled) {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 16px rgba(246, 138, 32, 0.4)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(246, 138, 32, 0.3)";
          }}
          title="Save changes to registration form"
        >
          {mutation?.isLoading ? (
            <>
              <span className="spinner-border spinner-border-sm" style={{ width: "14px", height: "14px" }} />
              Saving...
            </>
          ) : (
            <>
              <FaSave size={14} />
              Update Form
            </>
          )}
        </button>
      </div>

      {/* ✅ Submit Error Alert */}
      {submitError && (
        <div style={{
          background: "#fef2f2",
          border: "1px solid #fecaca",
          color: "#dc2626",
          padding: "12px 16px",
          borderRadius: "10px",
          marginBottom: "20px",
          fontSize: "14px",
          fontWeight: "500",
        }}>
          {submitError}
        </div>
      )}

      {/* ✅ Fixed Fields Card */}
      <div style={{
        background: "white",
        borderRadius: "16px",
        padding: "24px",
        marginBottom: "20px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        border: "1px solid rgba(226, 232, 240, 0.8)",
      }}>
        <h6 style={{
          fontSize: "14px",
          fontWeight: "700",
          color: "#1e293b",
          marginBottom: "20px",
          paddingBottom: "12px",
          borderBottom: "2px solid #f1f5f9",
        }}>
          Default Fields (Read-only)
        </h6>
        <Row className="g-3">
          {formContent.slice(0, 4).map((field) => (
            <Col key={field.id} xs={12} sm={6}>
              <label style={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#475569",
                marginBottom: "10px",
                display: "block",
              }}>
                {field.label}
              </label>
              <input
                type="text"
                value={field.label}
                className="form-control"
                disabled
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: "10px",
                  border: "2px solid #e2e8f0",
                  fontSize: "14px",
                  backgroundColor: "#f8fafc",
                  color: "#64748b",
                  cursor: "not-allowed",
                }}
              />
            </Col>
          ))}
        </Row>
      </div>

      {/* ✅ Dynamic Fields */}
      {formContent?.slice(4)?.map((field, index) => (
        <div
          key={field.id}
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "24px",
            marginBottom: "20px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
            border: "1px solid rgba(226, 232, 240, 0.8)",
            transition: "all 0.3s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.1)";
            e.currentTarget.style.transform = "translateY(-2px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)";
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          {/* Field Header: Label + Type Selector */}
          <div className="d-flex justify-content-between align-items-center" style={{ flexWrap: "wrap", gap: "16px", marginBottom: "20px" }}>
            <div style={{ flex: 1, minWidth: "200px" }}>
              {editedField === field.id ? (
                <input
                  type="text"
                  value={field.label}
                  onChange={(e) => editField(field.id, e.target.value)}
                  onBlur={() => setEditedField(null)}
                  className="form-control"
                  autoFocus
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: "10px",
                    border: "2px solid #f68a20",
                    fontSize: "15px",
                    fontWeight: "600",
                    color: "#1e293b",
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setEditedField(null);
                  }}
                />
              ) : (
                <label
                  onClick={() => setEditedField(field.id)}
                  style={{
                    fontSize: "15px",
                    fontWeight: "600",
                    color: "#1e293b",
                    cursor: "pointer",
                    display: "block",
                    padding: "12px 0",
                  }}
                >
                  {field.label}
                  <span style={{ fontSize: "12px", color: "#94a3b8", fontWeight: "400", marginLeft: "8px" }}>(Click to edit)</span>
                </label>
              )}
            </div>
            <select
              value={field.question_type}
              onChange={(e) => editFieldType(field.id, e.target.value)}
              style={{
                padding: "10px 16px",
                borderRadius: "10px",
                border: "2px solid #e2e8f0",
                fontSize: "13px",
                fontWeight: "600",
                backgroundColor: "white",
                cursor: "pointer",
                minWidth: "150px",
                color: "#475569",
              }}
            >
              <option value="short_answer">Short Answer</option>
              <option value="text">Text</option>
              <option value="paragraph">Paragraph</option>
              <option value="options">Options</option>
              <option value="checkbox">Checkbox</option>
            </select>
          </div>

          {field.question_type === "checkbox" && (
            <div style={{
              marginTop: "20px",
              padding: "16px",
              background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
            }}>
              <label style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                margin: 0,
                cursor: "default",
              }}>
                <input type="checkbox" disabled style={{ width: "18px", height: "18px" }} />
                <span style={{ fontSize: "14px", color: "#475569", fontWeight: "500" }}>
                  {field.label}
                </span>
              </label>
            </div>
          )}

          {/* Options Management (for "options" type) */}
          {field.question_type === "options" && (
            <div style={{ 
              marginTop: "20px", 
              padding: "20px", 
              background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)", 
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
            }}>
              <div className="d-flex" style={{ gap: "12px", marginBottom: "16px" }}>
                <input
                  type="text"
                  onChange={(e) => setTextField(e.target.value)}
                  value={textField}
                  placeholder="Add an option..."
                  className="form-control"
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    borderRadius: "10px",
                    border: "2px solid #e2e8f0",
                    fontSize: "14px",
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addFieldOption(field.id, textField);
                  }}
                />
                <button
                  onClick={() => addFieldOption(field.id, textField)}
                  disabled={!textField?.trim()}
                  style={{
                    padding: "12px 20px",
                    borderRadius: "10px",
                    border: "none",
                    background: !textField?.trim() 
                      ? "#cbd5e1" 
                      : "linear-gradient(135deg, #f68a20 0%, #fb923c 100%)",
                    color: "white",
                    cursor: !textField?.trim() ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontWeight: "600",
                    fontSize: "13px",
                    boxShadow: !textField?.trim() ? "none" : "0 2px 8px rgba(246, 138, 32, 0.3)",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (textField?.trim()) {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(246, 138, 32, 0.4)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(246, 138, 32, 0.3)";
                  }}
                >
                  <AiOutlinePlusCircle size={18} />
                  Add
                </button>
              </div>
              
              {field.list?.length > 0 ? (
                field.list.map((item, optionIndex) => (
                  <div
                    key={optionIndex}
                    className="d-flex align-items-center justify-content-between"
                    style={{
                      padding: "12px 16px",
                      background: "white",
                      borderRadius: "10px",
                      marginBottom: "10px",
                      border: "1px solid #e2e8f0",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#cbd5e1";
                      e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.05)";
                    }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <GoDotFill size={16} color="#f68a20" />
                      <span style={{ fontSize: "14px", color: "#475569", fontWeight: "500" }}>{item}</span>
                    </div>
                    <button
                      onClick={() => removeFieldOption(field.id, optionIndex)}
                      style={{
                        padding: "6px",
                        background: "none",
                        border: "none",
                        color: "#ef4444",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.2)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                      title="Remove option"
                    >
                      <TiDeleteOutline size={20} />
                    </button>
                  </div>
                ))
              ) : (
                <p style={{ 
                  fontSize: "13px", 
                  color: "#94a3b8", 
                  textAlign: "center", 
                  padding: "12px",
                  fontStyle: "italic"
                }}>
                  No options added yet. Add your first option above.
                </p>
              )}
            </div>
          )}

          {/* Action Buttons: Duplicate, Delete, Move */}
          <div className="d-flex justify-content-between align-items-center" style={{ 
            marginTop: "24px", 
            paddingTop: "20px", 
            borderTop: "2px solid #f1f5f9",
          }}>
            <div className="d-flex gap-2">
              <button
                onClick={() => duplicateQuestion(index + 4)}
                style={{
                  padding: "10px 14px",
                  borderRadius: "10px",
                  border: "2px solid #e2e8f0",
                  background: "white",
                  color: "#64748b",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "13px",
                  fontWeight: "500",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#cbd5e1";
                  e.currentTarget.style.backgroundColor = "#f8fafc";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e2e8f0";
                  e.currentTarget.style.backgroundColor = "white";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
                title="Duplicate this field"
              >
                <MdOutlineFileCopy size={16} />
                Duplicate
              </button>
              <button
                onClick={() => deleteQuestion(index + 4)}
                style={{
                  padding: "10px 14px",
                  borderRadius: "10px",
                  border: "2px solid #fee2e2",
                  background: "white",
                  color: "#ef4444",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "13px",
                  fontWeight: "500",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#fee2e2";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "white";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
                title="Delete this field"
              >
                <MdDeleteOutline size={16} />
                Delete
              </button>
            </div>
            <div className="d-flex gap-2">
              {index + 4 > 4 && (
                <button
                  onClick={() => moveFieldUp(index + 4)}
                  style={{
                    padding: "10px 14px",
                    borderRadius: "10px",
                    border: "2px solid #e2e8f0",
                    background: "white",
                    color: "#64748b",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "13px",
                    fontWeight: "500",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#cbd5e1";
                    e.currentTarget.style.backgroundColor = "#f8fafc";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.backgroundColor = "white";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  title="Move field up"
                >
                  <FaAngleDoubleUp size={14} />
                  Up
                </button>
              )}
              {index + 4 < formContent.length - 1 && (
                <button
                  onClick={() => moveFieldDown(index + 4)}
                  style={{
                    padding: "10px 14px",
                    borderRadius: "10px",
                    border: "2px solid #e2e8f0",
                    background: "white",
                    color: "#64748b",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "13px",
                    fontWeight: "500",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#cbd5e1";
                    e.currentTarget.style.backgroundColor = "#f8fafc";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.backgroundColor = "white";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  title="Move field down"
                >
                  Down
                  <FaAngleDoubleDown size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      ))}

      <OptInDisclaimerPreview />

      {/* ✅ Floating Add Button - Beautiful & Functional */}
      <button
        onClick={addQuestion}
        style={{
          position: "fixed",
          bottom: "32px",
          right: "32px",
          width: "64px",
          height: "64px",
          borderRadius: "50%",
          border: "none",
          background: "linear-gradient(135deg, #f68a20 0%, #fb923c 100%)",
          color: "white",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 6px 24px rgba(246, 138, 32, 0.4)",
          transition: "all 0.3s ease",
          zIndex: 999,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "scale(1.1) rotate(90deg)";
          e.currentTarget.style.boxShadow = "0 8px 32px rgba(246, 138, 32, 0.5)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1) rotate(0deg)";
          e.currentTarget.style.boxShadow = "0 6px 24px rgba(246, 138, 32, 0.4)";
        }}
        title="Add New Field"
      >
        <AiFillPlusCircle size={36} />
      </button>
    </div>
  );
};

export default EditRegistrationForm;